import React, { useMemo } from 'react'
import Link from 'next/link'
import { GuildShield } from '@/atoms/Icons/GuildShield'
import { CaptionMD, EyebrowLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { NamedColor } from '@/constants/types'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Avatar } from '@/molecules/Avatar'
import { Membership, useFetchUserMembership } from '@/services/MembershipService/MembershipService'
import { useUser } from '@/services/UserService'
import { FetchUserMembershipQuery } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { getUserFirstName, getUserLastName } from '@/utils/users'
import { formatShortPlanName, isGuildMember as isGuildMemberCheck, isInvestor } from '@/views/GuildGiftView/tiers'

interface Props {
  handleItemClick: React.MouseEventHandler<HTMLAnchorElement>
}

export const UserMenuHeader: React.FC<Props> = ({ handleItemClick }) => {
  const { t } = useTranslate('common')
  const { locale } = useLocale()
  const { user } = useUser()
  const { loading, data: rawData } = useFetchUserMembership()
  const { isDarkMode } = useThemeContext()
  // Safe to assert because we don't use @defer or returnPartialData for this query
  const data = rawData as FetchUserMembershipQuery | undefined
  const membership: Membership | null | undefined = data?.user?.myMembership
  const account: Account = useMemo(() => mapAccount(t, membership, isDarkMode), [membership, t, isDarkMode])

  const name = useMemo(() => {
    const firstName = getUserFirstName(user)
    const lastName = getUserLastName(user)

    if (firstName && lastName) {
      return `${firstName} ${lastName.charAt(0)}.`
    } else if (firstName) {
      return firstName
    }

    return ''
  }, [user])

  const isGuildMember = isGuildMemberCheck(membership)
  return (
    <div className="-mx-4 mb-3 flex gap-3 border-y border-core-gray-900 px-4 py-5 md:border-t-0">
      <div className="shrink-0">
        <Avatar size={48} />
      </div>
      <div className="flex w-full flex-wrap justify-between gap-x-3 gap-y-2">
        <div>
          <TitleMD weight="semibold" color={isDarkMode ? 'white' : 'core-gray-950'}>
            {name !== '' ? name : t('anonymous', 'Anonymous')}
          </TitleMD>
          {!!user?.email && <CaptionMD color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>{user.email}</CaptionMD>}
        </div>

        {!loading && (
          <div>
            <div className="flex items-center gap-1">
              {isGuildMember && <GuildShield size={12} />}
              <EyebrowLG weight="semibold" color={account.color} className="uppercase">
                {account.name}
              </EyebrowLG>
            </div>
            {isGuildMember && !!membership?.memberSince && (
              <CaptionMD color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>
                {t('sinceDate', 'Since {{ date }}', {
                  date: new Date(membership.memberSince).toLocaleDateString(locale, { dateStyle: 'short' }),
                })}
              </CaptionMD>
            )}
            {!isGuildMember && (
              <Link href={paths.guild.join} onClick={handleItemClick}>
                <CaptionMD color="core-oxide-bright">{t('unlockBenefits', 'Unlock Benefits')}</CaptionMD>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface Account {
  name: string
  color: NamedColor
}

function mapAccount(t: TranslateFunction, membership: Membership | undefined | null, isDarkMode: boolean): Account {
  const planName = formatShortPlanName(t, membership?.primaryProduct?.tier)
  return planName
    ? {
        name: planName,
        color: 'core-copper',
      }
    : isInvestor(membership)
    ? {
        name: t('investor', 'Investor'),
        color: isDarkMode ? 'white' : 'core-gray-950',
      }
    : {
        name: t('freeAccount', 'Free Account'),
        color: isDarkMode ? 'core-gray-400' : 'core-gray-700',
      }
}
